<template>
  <div class="pt-1 px-4 profile-body">
    <v-alert v-if="notFound" type="error" class="mt-2">
      <div>Client non trouvé!</div>
      <v-btn outlined class="mt-2" @click="homepage">Retour à la page d'accueil</v-btn>
    </v-alert>

    <div v-else>
      <div class="d-flex align-center pb-1">
        <v-chip class="px-0 mr-2" color="transparent" @click="homepage()">
          <v-icon>mdi-chevron-left</v-icon>
        </v-chip>
        <h2 class="d-none d-inline-block mr-2 pt-1">
          Profil Client
        </h2>
        <v-spacer />

        <v-checkbox v-if="isReturnToPegase || isReturnToHello" v-model="isCustomerSelected" label="ajouter au planificateur" @change="selectCustomer" />
        <v-select
          v-if="canShowImpersonateButtons"
          v-model="selectedWorkspace"
          item-text="label"
          item-value="code"
          :items="workspaces"
          label="Choisir le centre"
          outlined
          hide-details
          dense
          flat
          return-object
          solo
          style="max-width: 200px"
        />
        <v-tooltip v-if="canShowImpersonateButtons" top>
          <template #activator="{ on, attrs }">
            <v-btn :disabled="!selectedWorkspace" v-bind="attrs" icon v-on="on" @click="redirectToImpersonate()">
              <v-icon> mdi-eye-outline </v-icon>
            </v-btn>
          </template>
          <span>Démarrer impersonate</span>
        </v-tooltip>
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn v-bind="attrs" icon v-on="on" @click="duplicateMode()">
              <v-icon> mdi-content-copy </v-icon>
            </v-btn>
          </template>
          <span>Duplication fiche contact</span>
        </v-tooltip>
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn v-bind="attrs" icon v-on="on" @click="exportCustomer()">
              <v-icon> mdi-file-export-outline </v-icon>
            </v-btn>
          </template>
          <span>Export csv</span>
        </v-tooltip>
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn v-bind="attrs" icon :disabled="loading" v-on="on" @click="showVersionHistory = true">
              <v-icon> mdi-history</v-icon>
            </v-btn>
          </template>
          <span>Historique du Client</span>
        </v-tooltip>
      </div>

      <v-card elevation="5" class="rounded-lg">
        <v-tabs v-model="tab" elevation="3" background-color="primary" dark height="64px" show-arrows>
          <v-tabs-slider />
          <v-tab v-for="tab in tabItemsPerProject" :key="tab.label">{{ tab.label }}</v-tab>
        </v-tabs>
        <v-card-text class="pa-0">
          <v-tabs-items v-model="tab">
            <v-tab-item v-for="tabItem in tabItemsPerProject" :key="tabItem.label">
              <div v-if="!loading">
                <component
                  :is="tabItem.component"
                  :content="content"
                  :save-customer="saveCustomer"
                  :add-context-to-user="addContextToUser"
                  :add-exist-iresa-id-to-user="addExistIresaIdToUser"
                  :remove-iresa-id="removeIresaId"
                  :loading="loading"
                  :errors="errors"
                  :successes="successes"
                />

                <AddContact :show="showAddContactDialog" :add-contact="addContact" :contact-to-add="contactToAdd" />
              </div>
              <div v-else>
                <v-skeleton-loader class="ma-4" elevation="1" type="table-heading, table-tbody" />
                <v-skeleton-loader class="ma-4" elevation="1" type="table-heading, table:  button" />
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </div>
    <ProfileVersionHistory v-if="!loading" :uuid="content.uuid" :show-version-history="showVersionHistory" @close="showVersionHistory = false" />
  </div>
</template>

<script>
import SearchPartner from '@/components/search/SearchPartner';
import OrdersAlphaList from '@/components/profile/OrdersAlphaList';
import OrdersIResaList from '@/components/profile/OrdersIResaList';
import ReferralsList from '@/components/profile/ReferralsList';
import ReferralCodesList from '@/components/profile/ReferralCodesList';
import AllowedProducts from '@/components/profile/AllowedProducts';
import AddContact from '@/components/profile/AddContact';

import ProfileSpeedboatContact from '@/views/pages/profile/ProfileSpeedboatContact';
import ProfileLabalaguereContact from '@/views/pages/profile/ProfileLabalaguereContact';

import ProfileSpeedboatIdentities from '@/views/pages/profile/ProfileSpeedboatIdentities';

import ProfileSpeedboatTransactions from '@/views/pages/profile/ProfileSpeedboatTransactions';
import ProfileLabalaguereTransactions from '@/views/pages/profile/ProfileLabalaguereTransactions';

import ProfileInteractions from '@/views/pages/profile/ProfileInteractions';
import ProfileVersionHistory from '@/views/pages/profile/ProfileVersionHistory';

import { utils, requests } from '@/mixins/index';
import { EventBus } from '../../mixins';

export default {
  name: 'Customer',
  components: {
    SearchPartner,
    OrdersAlphaList,
    OrdersIResaList,
    ReferralsList,
    ReferralCodesList,
    AddContact,
    AllowedProducts,
    ProfileSpeedboatContact,
    ProfileLabalaguereContact,
    ProfileSpeedboatIdentities,
    ProfileSpeedboatTransactions,
    ProfileLabalaguereTransactions,
    ProfileInteractions,
    ProfileVersionHistory
  },
  mixins: [utils, requests],
  beforeRouteUpdate (to, from, next) { // On update route to same route
    next();
    if (from.path === to.path) {
      return null;
    }
    // Reset state of the component
    const newInitialState = this.initialState();
    Object.assign(this.$data, newInitialState);

    this.loadProfile(to.params.uuid);
  },

  data () {
    return this.initialState();
  },
  computed: {
    tabItemsPerProject () {
      const userRoles = this.$store.state.user.roles || [];

      return this.tabsItems[this.$store.getters.getBrand].filter(tab => {
        const hasDigitalAccess = !tab.digitalAccountRequired || this.content.sso_id;
        const hasRoleAccess = userRoles.includes('admin') || !tab.roles || userRoles.some(userRole => tab.roles.includes(userRole));
        return hasDigitalAccess && hasRoleAccess;
      });
    },
    canShowImpersonateButtons () {
      if (!this.content.sso_id) {
        return false;
      }

      const userHasContext = (this.content.contexts || []).some(c => ['alpha', 'speedboat', 'labalaguere', 'pegase'].includes(c.value));

      const roles = ['admin', 'alpha_impersonate_mode', 'speedboat_impersonate_mode', 'labalaguere_impersonate_mode'];

      const collaboratorHasPermission = (this.$store.state.user.roles || []).some(role => roles.includes(role));

      return (userHasContext || this.workspaces.length) && collaboratorHasPermission;
    },
    workspaces () {
      const speedboatWorkspaces = (this.content.contexts || []).map(c => c.value).includes('speedboat') ? [
        {
          code: 'speedboat',
          label: 'SPEEDBOAT',
          siteUrl: '',
          project: 'speedboat'
        }
      ] : [];

      const alphaWorkspaces = this.$store.state.alphaWorkspaces.filter(workspace => {
        if ((this.content.contexts || []).some(context => ['alpha', 'pegase'].includes(context.value))) {
          return true;
        }

        if ((this.content.customer_workspaces || []).includes(workspace.code)) {
          return true;
        }

        return false;
      });

      const labalaguereWorkspaces = (this.content.contexts || []).map(c => c.value).includes('labalaguere') ? [
        {
          code: 'labalaguere',
          label: 'LABALAGUERE',
          siteUrl: '',
          project: 'labalaguere'
        }
      ] : [];

      if ((this.$store.state.user.roles || []).includes('admin')) {
        return [...speedboatWorkspaces, ...alphaWorkspaces, ...labalaguereWorkspaces];
      }

      let workspaces = [];

      if ((this.$store.state.user.roles || []).includes('speedboat_impersonate_mode')) {
        workspaces = [...workspaces, ...speedboatWorkspaces];
      }
      if ((this.$store.state.user.roles || []).includes('labalaguere_impersonate_mode')) {
        workspaces = [...workspaces, ...labalaguereWorkspaces];
      }

      if ((this.$store.state.user.roles || []).includes('alpha_impersonate_mode')) {

        if(this.$store.state.user.readWorkspaces.includes('*')) {
          workspaces = [...workspaces, ...alphaWorkspaces];
        } else {
          workspaces = [...workspaces, ...(alphaWorkspaces || []).filter(workspace => this.$store.state.user.readWorkspaces.includes(workspace.code))];
        }

        if(workspaces.length === 1) {
          this.selectedWorkspace = workspaces[0];
        }
      }

      return workspaces;
    },
    isReturnToPegase () {
      return this.$store.getters.isReturnToPegase;
    },
    isReturnToHello () {
      return this.$store.getters.isReturnToHello;
    },
    contactToAdd () {
      return this.$store.state.addContactMode.contactToAdd;
    },
    showAddContactDialog () {
      return (this.$store.state.addContactMode.customer_uuid === this.$route.params.uuid) && !!this.contactToAdd;
    },
    isProd () {
      return process.env.VUE_APP_ENVIRONMENT === 'production';
    }
  },
  mounted () {
    this.loadProfile(this.$route.params.uuid);
    EventBus.$on('user-updated', () => {
      this.getProfile(this.content.uuid);
    });
    EventBus.$on('change-errors', newErrors => {
      this.errors = newErrors;
    });
    EventBus.$on('change-successes', newSuccesses => {
      this.successes = newSuccesses;
    });
    EventBus.$on('change-context', newContent => {
      this.content = newContent;
    });
    EventBus.$on('updateCustomerIresaIds', iresaIds => {
      this.content.iresa_ids = iresaIds;
    });
  },
  methods: {
    async redirectToImpersonate () {
      this.loading = true;

      const response = await this.impersonateMode(this.content, this.$store.state.user, this.selectedWorkspace);

      if (!response.success) {
        const notification = {
          message: response.message,
          type: 'danger',
          timeout: 16000
        };
        EventBus.$emit('show-notifications', [notification]);
        this.loading = false;
        return;
      }

      const domain = this.selectedWorkspace.project === 'labalaguere' ? process.env.VUE_APP_LABALAGUERE_DOMAIN : process.env.VUE_APP_UCPA_DOMAIN;

      let url = `${domain}/api/impersonateMode?data=${encodeURI(response.data)}`;

      if (this.selectedWorkspace.project === 'alpha') {
        url = `${domain}/${this.selectedWorkspace.sitePreRoute}/api/impersonateMode/${this.selectedWorkspace.code}?data=${encodeURI(response.data)}`;
      }

      window.open(
        url,
        '_blank'
      );
      this.loading = false;
    },
    selectCustomer (selected) {
      if (selected) {
        this.$store.commit('addSelectedCustomer', this.content);
        this.$store.commit('removeDeselectedCustomersCustomer', this.content);
        return;
      }
      this.$store.commit('removeSelectedCustomer', this.content);
      this.$store.commit('addDeselectedCustomersCustomer', this.content);
    },
    async addContact () {
      await this.addContactoCustomerRequest(this.content.uuid, this.contactToAdd.uuid);
    },
    duplicateMode () {
      this.$store.commit('setDuplicateContactMode', {
        active: true,
        customer: this.content
      });
      this.newCustomer();
    },
    loadProfile (uuid) {
      if (this.$route.name === 'CustomerBy') {
        this.findProfile();
      } else {
        this.getProfile(uuid);
      }
    },
    async findProfile () {
      let filters = null;

      if (this.$route.params.facet === 'iresa_id') {
        filters = `iresa_ids.iresa_id:${this.$route.params.value}`;
      }

      if (filters) {
        const customerResponse = await this.getCustomers(this.$route.params.value);
        if (customerResponse.data.items.length === 0) {
          this.notFound = true;
          return;
        }

        const customer = customerResponse.data.items[0];

        this.getProfile(customer.uuid);
      } else {
        // IF NOT FOUND
        this.notFound = true;
      }
    },
    async getProfile (uuid) {
      this.notFound = false;
      this.loading = true;

      const responseCustomer = await this.getCustomer(uuid);
      if (!responseCustomer.data) {
        this.notFound = true;
        return;
      }

      this.originalCustomer = JSON.parse(
        JSON.stringify(responseCustomer.data)
      );

      this.content = responseCustomer.data;

      // set brand equal to user workspace
      this.$store.commit('setBrand', this.content.workspace);

      if (!responseCustomer.success) {
        this.error = responseCustomer.message;
      }

      this.loading = false;
    },
    async saveCustomer (newContent, aux = {}) {
      this.editCostumer = false;
      
      // Remove fields that are not in the updateInput
      if (newContent.newsletters && newContent.newsletters.hasOwnProperty('ucpa_last_update_time')) {
        delete newContent.newsletters.ucpa_last_update_time;
      }
      if (newContent.newsletters && newContent.newsletters.hasOwnProperty('loisirs_last_update_time')) {

        delete newContent.newsletters.loisirs_last_update_time;
      }

      const responseCustomer = await this.saveCustomerRequest(newContent);

      this.loading = false;

      if (!responseCustomer.success) {
        this.errors = ['Error in the request.'];
        return false;
      }

      if (responseCustomer.data.customer) {
        this.content = responseCustomer.data.customer;
      } else {
        this.content = this.originalCustomer;
      }

      if (responseCustomer.data.success) {
        this.successes = aux.successMessage ? [aux.successMessage] : ['Contact sauvegardé'];
        return true;
      }

      const notification = {
        message: null,
        type: 'warning',
        timeout: 16000
      };

      if (responseCustomer.data.code === 'FIELDS_WITH_INVALID_FORMAT') {
        notification.message = "Format invalide dans le numéro tél ou Mobile, corriger information à jour le client avant d'activer le Compte digital";
      }

      if (responseCustomer.data.code === 'MISSING_REQUIRED_FIELDS') {
        notification.message = "Veuillez remplir toutes les informations obligatoires.";
      }

      if (!notification.message) {
        notification.link = `/?email=${encodeURIComponent(this.content.email)}`;
        notification.linkText = 'Une autre fiche contact existe déjà avec ces coordonnées, cliquez ici pour la retrouver';
      }

      EventBus.$emit('show-notifications', [notification]);
      this.successes = [];
      this.errors = ['La mise à jour du contact a échoué'];

      return false;
    },
    async exportCustomer () {
      this.loading = true;
      const responseExport = await this.exportCustomerRequest(this.content.uuid);

      this.loading = false;

      if (!responseExport.success) {
        this.errors = ['Error in the request.'];
        return false;
      }

      this.successes = ['Vous recevrez un e-mail avec une pièce jointe csv.'];
      return true;

      //   this.loading = false;
      // } else {
      //   this.errors = ['Erreur dans un des champs du formulaire'];
    },
    async addExistIresaIdToUser (context, iresaId) {
      this.loading = true;

      const body = {
        uuid: this.content.uuid,
        context: context,
        iresaId: iresaId
      };

      const response = await this.addIresaIdToCustomerRequest(body);

      const notification = { type: response.success ? 'success' : 'danger' };

      if (!response.success) {
        notification.message = 'Error in the request.';

        switch (response.code) {
          case 'CUSTOMER_NOT_FOUND':
            notification.message = "Le client avec l'identifiant actuel n'a pas été trouvé.";
            break;

          case 'CUSTOMER_HAS_IRESA_ID':
            notification.message = "Le client possède déjà l'identifiant iresa.";
            break;

          case 'INVALID_IRESA_ID':
            notification.message = "L'identifiant Iresa n'existe pas.";
            break;

          case 'IRESA_ID_NOT_MATCH_CONTEXT':
            notification.message = "L'identifiant Iresa ne correspond pas au partenaire sélectionné.";
            break;

          case 'CUSTOMER_WITH_IRESA_ID_EXIST':
            const matches = response.message.match(/\bcustomer_[a-zA-Z0-9_-]+/);
            if (matches) {
              notification.link = `/customer/${matches[0]}`;
              notification.linkText = 'Ouvrir un dossier client.';
            }

            notification.message = "Le client avec l'identifiant iresa existe déjà dans le CRM.";
            break;

          default:
            notification.message = 'Erro sur la demande.';
            break;
        }
      }

      if (response.success) {
        notification.message = "Identité d'Iresa ajoutée au client.";
        this.content.iresa_ids = response.data.iresa_ids;
      }

      EventBus.$emit('show-notifications', [notification]);

      this.loading = false;
    },
    async removeIresaId (iresaId, context) {
      this.loading = true;
      const body = {
        context: context,
        iresaId: iresaId,
        uuid: this.content.uuid
      };

      const response = await this.removeIresaIdToCustomerRequest(body);

      const notification = { type: 'danger' };

      if (!response.success) {
        notification.message = 'Error in the request.';
        EventBus.$emit('show-notifications', [notification]);
        this.loading = false;
        return;
      }

      this.content.iresa_ids = response.data.iresa_ids;
      notification.type = 'success';
      notification.message = 'Iresa ID supprimé succès';
      EventBus.$emit('show-notifications', [notification]);
      this.loading = false;
    },
    async addContextToUser (context) {
      this.loading = true;
      const body = {
        uuid: this.content.uuid,
        workspace: 'crm',
        gender: this.content.gender,
        firstname: this.content.firstname,
        lastname: this.content.lastname,
        email: this.content.email,
        birth_date: this.content.birth_date,
        context: context
      };
      const responseCustomer = await this.saveCustomerRequest(body);

      const notification = { type: 'danger' };

      if (!responseCustomer.success) {
        notification.message = 'Error in the request.';
        EventBus.$emit('show-notifications', [notification]);
      }

      if (responseCustomer.data.success) {
        EventBus.$emit('change-context', responseCustomer.data.customer);

        notification.message = 'Le contexte a été ajouté';
        notification.type = 'success';
        EventBus.$emit('show-notifications', [notification]);
      } else if (responseCustomer.data.code === 'CONTEXT_CREATION_ERROR') {
        notification.text = 'Error creating context, try again!';
        EventBus.$emit('show-notifications', [notification]);
      }

      this.loading = false;
    },
    initialState: function () {
      return {
        selectedWorkspace: null,
        isCustomerSelected: !!this.$store.state.selectedCustomers.find(c => c.uuid === this.$route.params.uuid),
        loading: true,
        errors: [],
        successes: [],
        tab: 0,
        tabsItems: {
          crm: [
            { label: 'Contact', component: 'ProfileSpeedboatContact' },
            { label: 'Comptes Secondaires', component: 'ProfileSpeedboatIdentities' },
            { label: 'Transactions', component: 'ProfileSpeedboatTransactions' },
            { label: 'Intéractions', component: 'ProfileInteractions', digitalAccountRequired: true, roles: ['collaborator', 'hello', 'target', 'crm_interactions_reader', 'crm_interactions_writer'] } // Roles defined on the hello chat
          ],
          crm_labalaguere: [
            { label: 'Contact', component: 'ProfileLabalaguereContact' },
            { label: 'Transactions', component: 'ProfileLabalaguereTransactions' },
            { label: 'Intéractions', component: 'ProfileInteractions', digitalAccountRequired: true, roles: ['hello'] }
          ]
        },
        content: {},
        notFound: false,
        // --------------------
        error: null,
        originalCustomer: {},
        showVersionHistory: false
      };
    }
  }
};
</script>

<style lang="scss">
.profile-body {
  margin: 0 10%;
}

.order-table {
  max-height: 300px;
  overflow-x: hidden;
  overflow-y: scroll;
}

.user-card {

  .theme--light.v-input--is-disabled,
  .theme--light.v-input--is-disabled input,
  .theme--light.v-input--is-disabled textarea {
    color: rgba(0, 0, 0, 0.78);
  }
}
</style>
